/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef, useState} from 'react';
import {loadModules} from 'esri-loader';
import {error} from '@ion/core/src/logger/logger';
import {BasemapTypes} from '../types/types';

type UseBasemapGalleryProps = {
  view: __esri.MapView;
  createOSMBasemap: Function;
}

/**
 * Provides a hook for adding an ESRI JS 4 basemap gallery widget to a map/view.
 *
 * ```typescript
 *
 *    export const BasemapGalleryTool = ({map, view, ...}: Props) => {
 *      const basemapGalleryRef = useBasemapGallery(view);
 *      ...
 *    };
 *
 * ```
 *
 */
export const useBasemapGallery = ({view, createOSMBasemap}: UseBasemapGalleryProps) => {
  const basemapGalleryRef = useRef(null);
  const [basemapGallery, setBasemapGallery] = useState(null);

  useEffect(() => {
    if (!basemapGalleryRef.current) {
      loadModules(['esri/widgets/BasemapGallery', 'esri/Basemap'])
        .then(([BasemapGallery, Basemap]) => {
          const osmBasemap = createOSMBasemap?.();
          basemapGalleryRef.current = new BasemapGallery({
            id     : 'basemap-gallery-widget',
            source : [
              Basemap.fromId(BasemapTypes.grayVector),
              Basemap.fromId(BasemapTypes.hybrid),
              Basemap.fromId(BasemapTypes.satellite),
              osmBasemap,
              Basemap.fromId(BasemapTypes.streetsNavigationVector),
              Basemap.fromId(BasemapTypes.topoVector)
            ],
            visible : false,
            view
          });
        })
        .then(() => {
          setBasemapGallery(basemapGalleryRef);
        })
        .catch((err) => error(err));
    }
  }, [view]);

  return basemapGallery;
};

export default useBasemapGallery;
